import React, { useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAnalytics } from '@/hooks/useAnalyticsWithUser'
import { useLocation } from 'react-router-dom'

export const AnalyticsTracker: React.FC = () => {
  const { user, isAuthenticated, organization } = useAuth()
  const { identify, reset, trackPageView, trackUserAction } = useAnalytics()
  const location = useLocation()

  // Track authentication events
  useEffect(() => {
    if (isAuthenticated && user) {
      // Identify user in PostHog
      identify(user.id.toString(), {
        email: user.email,
        name: user.name,
        role: user.role,
        organization_id: organization?.id,
        organization_name: organization?.name,
        organization_type: organization?.plan?.type,
      })

      // Track login event
      trackUserAction('login', {
        user_id: user.id,
        organization_id: organization?.id,
        role: user.role,
      })
    } else if (!isAuthenticated) {
      // Reset PostHog on logout
      reset()
    }
  }, [isAuthenticated, user, organization, identify, reset, trackUserAction])

  // Track page views
  useEffect(() => {
    if (isAuthenticated) {
      const pageName = getPageName(location.pathname)
      trackPageView(pageName, {
        path: location.pathname,
        organization_id: organization?.id,
      })
    }
  }, [location.pathname, isAuthenticated, organization?.id, trackPageView])

  return null // This component doesn't render anything
}

// Helper function to get readable page names
const getPageName = (pathname: string): string => {
  if (pathname === '/') return 'dashboard'
  if (pathname.startsWith('/preview/')) return 'file_preview'
  if (pathname.startsWith('/search')) return 'search'
  if (pathname.startsWith('/trash')) return 'trash'
  if (pathname.startsWith('/recently-viewed')) return 'recently_viewed'
  if (pathname.startsWith('/profile')) return 'profile'
  if (pathname.startsWith('/account')) return 'account'
  if (pathname.startsWith('/organization')) return 'organization'
  if (pathname.startsWith('/admin')) return 'admin'
  if (pathname === '/login') return 'login'
  if (pathname === '/forgot-password') return 'forgot_password'
  if (pathname === '/reset-password') return 'reset_password'

  // Default fallback
  return pathname.replace('/', '').replace(/\//g, '_') || 'unknown'
}
